import { useParams, Link } from "react-router-dom";
import { Clock, TrendingUp, FolderOpen, MessageSquare, FileText, Rocket } from "lucide-react";
import AppleSection from "@/components/AppleSection";
import AppleSplitComparison from "@/components/AppleSplitComparison";
import Footer from "@/components/Footer";
import { blogPosts } from "@/data/blogPosts";
import { typography } from "@/styles/typography";
import { useSEO } from "@/hooks/useSEO";

interface SolutionBlock {
  title: string;
  bigNumber: string;
  comparison: string;
  description: string;
  ctaText: string;
  ctaLink: string;
}

interface CaseStudy {
  quote: string;
  author: string;
  role: string;
  metrics: Array<{ value: string; label: string }>;
}

interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

interface ServiceConfig {
  hero: { title: string; subtitle: string };
  problem: { bigStat: string; context: string };
  blocks: SolutionBlock[];
  caseStudy: CaseStudy;
  process: ProcessStep[];
  comparison: Array<{ before: string; after: string }>;
  finalCTA: { question: string };
  relatedCategories: string[];
  seo: { title: string; description: string };
}

const serviceConfig: Record<string, ServiceConfig> = {
  automatizacion: {
    hero: {
      title: "Automatización de Procesos.",
      subtitle: "Menos tareas. Más resultados. Todo automático."
    },
    problem: {
      bigStat: "El 65% del tiempo",
      context: "de una pyme se va en tareas repetitivas que no generan valor. Emails, facturas, seguimientos, copiar datos de un sitio a otro. Tiempo que nunca vuelve."
    },
    blocks: [
      {
        title: "Flujos que trabajan mientras tú no estás",
        bigNumber: "-80% tiempo",
        comparison: "En tareas administrativas repetitivas",
        description: "Automatiza emails de seguimiento, generación de facturas, actualización de CRMs y bases de datos. Sin intervención humana.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=automatizacion"
      },
      {
        title: "Errores que desaparecen",
        bigNumber: "0 errores",
        comparison: "En procesos automatizados vs manuales",
        description: "Los procesos manuales generan errores. La automatización los elimina. Datos siempre correctos, plazos siempre cumplidos.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=automatizacion"
      },
      {
        title: "Escala sin contratar",
        bigNumber: "×3 capacidad",
        comparison: "Con el mismo equipo",
        description: "Haz más con menos. Un equipo de 3 personas rinde como uno de 9 cuando las tareas repetitivas las hace la IA.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=automatizacion"
      }
    ],
    caseStudy: {
      quote: "Pasamos de dedicar 3 horas diarias a facturación a cero. Literalmente. La automatización se encarga de todo.",
      author: "Gerente de Operaciones",
      role: "Empresa de servicios B2B",
      metrics: [
        { value: "-80%", label: "Tiempo admin" },
        { value: "0", label: "Errores" },
        { value: "×3", label: "Capacidad" }
      ]
    },
    process: [
      { number: "01", title: "Analizamos tus procesos", description: "Identificamos qué tareas consumen más tiempo y cuáles se pueden automatizar con mayor impacto." },
      { number: "02", title: "Diseñamos los flujos", description: "Creamos automatizaciones a medida que se integran con tus herramientas actuales." },
      { number: "03", title: "Activamos y optimizamos", description: "Ponemos todo en marcha y ajustamos hasta que funcione como un reloj." }
    ],
    comparison: [
      { before: "3h/día en emails", after: "0 minutos" },
      { before: "Errores en facturas", after: "Facturación perfecta" },
      { before: "Copiar datos a mano", after: "Sincronización automática" }
    ],
    finalCTA: { question: "¿Listo para recuperar tu tiempo?" },
    relatedCategories: ["Automatización", "Gestión Empresarial"],
    seo: {
      title: "Automatización de Procesos con IA | Globaltis IA",
      description: "Elimina tareas repetitivas y multiplica la capacidad de tu equipo. Automatización inteligente para pymes que quieren crecer sin complicaciones."
    }
  },
  "atencion-cliente": {
    hero: {
      title: "Atención al Cliente 24/7.",
      subtitle: "Siempre disponible. Siempre resolviendo."
    },
    problem: {
      bigStat: "El 40% de las llamadas",
      context: "se pierden fuera de horario. Cada llamada perdida es un cliente que se va a la competencia. Y nunca vuelve."
    },
    blocks: [
      {
        title: "Responde siempre, a cualquier hora",
        bigNumber: "24/7",
        comparison: "Sin ampliar horarios ni personal",
        description: "Agentes de voz y chat que atienden consultas, agendan citas y resuelven dudas. De día y de noche. Fin de semana incluido.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=atencion-cliente"
      },
      {
        title: "WhatsApp que convierte",
        bigNumber: "×5 respuestas",
        comparison: "Más rápido que un equipo humano",
        description: "Respuestas instantáneas en WhatsApp que cualifican leads, envían información y cierran citas. Sin esperas.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=atencion-cliente"
      },
      {
        title: "Cero llamadas perdidas",
        bigNumber: "100%",
        comparison: "De llamadas atendidas",
        description: "La IA contesta cada llamada, recoge los datos y te avisa solo cuando es necesario. Tú decides cuándo intervenir.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=atencion-cliente"
      }
    ],
    caseStudy: {
      quote: "Desde que activamos el agente de voz, no hemos perdido ni una sola llamada. Los clientes están más contentos y nosotros más tranquilos.",
      author: "Propietario",
      role: "Clínica dental",
      metrics: [
        { value: "0", label: "Llamadas perdidas" },
        { value: "+35%", label: "Citas agendadas" },
        { value: "24/7", label: "Disponibilidad" }
      ]
    },
    process: [
      { number: "01", title: "Definimos tu flujo de atención", description: "Analizamos cómo atiendes hoy y qué preguntas se repiten más para diseñar el agente perfecto." },
      { number: "02", title: "Entrenamos al agente", description: "Le enseñamos tu negocio, tus servicios y tu forma de hablar para que suene natural." },
      { number: "03", title: "Lo activamos en tus canales", description: "Teléfono, WhatsApp, web. Donde estén tus clientes, ahí estará tu agente." }
    ],
    comparison: [
      { before: "Llamadas sin contestar", after: "100% atendidas" },
      { before: "Horario limitado", after: "Disponible 24/7" },
      { before: "Respuestas lentas", after: "Respuesta instantánea" }
    ],
    finalCTA: { question: "¿Cuántas llamadas has perdido esta semana?" },
    relatedCategories: ["Automatización", "Herramientas IA"],
    seo: {
      title: "Atención al Cliente 24/7 con IA | Globaltis IA",
      description: "Agentes de voz y WhatsApp que atienden a tus clientes 24/7. Cero llamadas perdidas, más citas y mejor experiencia. Para pymes que no pueden permitirse perder clientes."
    }
  },
  "contenido-ia": {
    hero: {
      title: "Contenido Profesional con IA.",
      subtitle: "Más contenido. Menos esfuerzo. Mejor calidad."
    },
    problem: {
      bigStat: "15 horas por semana",
      context: "es lo que dedica de media una pyme a crear contenido. Y aun así, nunca es suficiente. Redes sociales, blog, presentaciones, vídeos... la lista no para."
    },
    blocks: [
      {
        title: "Vídeos profesionales en minutos",
        bigNumber: "10 min",
        comparison: "En lugar de 2 días por vídeo",
        description: "Genera vídeos con presentador virtual, subtítulos y música. Listos para redes, web o formación interna.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=contenido-ia"
      },
      {
        title: "Diseños que no necesitan diseñador",
        bigNumber: "×20 piezas",
        comparison: "Al mes sin equipo de diseño",
        description: "Imágenes, banners, posts para redes y presentaciones. Todo con tu marca, todo coherente, todo automático.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=contenido-ia"
      },
      {
        title: "Textos que conectan",
        bigNumber: "-90% tiempo",
        comparison: "En redacción de contenido",
        description: "Artículos, newsletters, descripciones de producto. La IA escribe el borrador, tú le das el toque final.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=contenido-ia"
      }
    ],
    caseStudy: {
      quote: "Antes tardábamos una semana en preparar el contenido del mes. Ahora lo tenemos en una mañana. Y con mejor calidad.",
      author: "Responsable de Marketing",
      role: "Empresa de retail",
      metrics: [
        { value: "×10", label: "Velocidad" },
        { value: "-85%", label: "Coste" },
        { value: "+300%", label: "Contenido/mes" }
      ]
    },
    process: [
      { number: "01", title: "Definimos tu identidad", description: "Analizamos tu marca, tono de voz y estilo visual para que todo el contenido sea coherente." },
      { number: "02", title: "Configuramos los flujos", description: "Creamos plantillas y automatizaciones para cada tipo de contenido que necesitas." },
      { number: "03", title: "Producimos a escala", description: "Generamos contenido de forma continua. Tú solo apruebas y publicas." }
    ],
    comparison: [
      { before: "1 vídeo al mes", after: "10 vídeos al mes" },
      { before: "Diseñador freelance", after: "Diseños automáticos" },
      { before: "Semanas de producción", after: "Horas de producción" }
    ],
    finalCTA: { question: "¿Cuánto contenido podrías crear si tuvieras un equipo de IA?" },
    relatedCategories: ["Herramientas IA", "Creación de Contenido"],
    seo: {
      title: "Contenido Profesional con IA | Globaltis IA",
      description: "Vídeos, diseños y textos profesionales generados con IA. Multiplica tu producción de contenido sin ampliar equipo. Resultados desde la primera semana."
    }
  },
  "presencia-digital": {
    hero: {
      title: "Presencia Digital Express.",
      subtitle: "Online en días, no meses."
    },
    problem: {
      bigStat: "El 60% de las pymes",
      context: "no tiene una web actualizada o directamente no tiene presencia online. Mientras tanto, sus clientes buscan en Google antes de comprar."
    },
    blocks: [
      {
        title: "Tu web lista en días",
        bigNumber: "5 días",
        comparison: "En lugar de 3 meses con una agencia",
        description: "Webs profesionales creadas con IA que se adaptan a tu negocio. Responsive, rápidas y optimizadas para Google.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=presencia-digital"
      },
      {
        title: "Landing pages que convierten",
        bigNumber: "+40%",
        comparison: "De conversión vs webs genéricas",
        description: "Páginas de aterrizaje diseñadas para captar leads. Con formularios, CTAs y seguimiento automático.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=presencia-digital"
      },
      {
        title: "SEO desde el primer día",
        bigNumber: "Top 10",
        comparison: "En búsquedas locales en 90 días",
        description: "Cada página está optimizada para que te encuentren. Contenido SEO, estructura correcta y velocidad de carga.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=presencia-digital"
      }
    ],
    caseStudy: {
      quote: "En una semana teníamos la web funcionando y ya estábamos recibiendo formularios de contacto. Increíble.",
      author: "Fundador",
      role: "Startup de servicios",
      metrics: [
        { value: "5 días", label: "Web lista" },
        { value: "+200%", label: "Visibilidad" },
        { value: "×3", label: "Leads/mes" }
      ]
    },
    process: [
      { number: "01", title: "Entendemos tu negocio", description: "Definimos objetivos, público y mensajes clave para tu presencia digital." },
      { number: "02", title: "Diseñamos con IA", description: "Creamos tu web o landing page con tecnología IA, adaptada a tu marca." },
      { number: "03", title: "Lanzamos y medimos", description: "Publicamos, conectamos analíticas y optimizamos para conseguir resultados." }
    ],
    comparison: [
      { before: "Sin web o web antigua", after: "Web profesional moderna" },
      { before: "3 meses de desarrollo", after: "5 días hasta lanzamiento" },
      { before: "Invisible en Google", after: "Posicionado en búsquedas" }
    ],
    finalCTA: { question: "¿Cuántos clientes te pierdes por no estar online?" },
    relatedCategories: ["Desarrollo", "Marketing Digital"],
    seo: {
      title: "Presencia Digital Express con IA | Globaltis IA",
      description: "Web profesional en 5 días con IA. Landing pages que convierten y SEO desde el primer día. La forma más rápida de poner tu negocio online."
    }
  },
  "gestion-administrativa": {
    hero: {
      title: "Gestión Administrativa Digital.",
      subtitle: "Menos papeleo. Más control."
    },
    problem: {
      bigStat: "El 50% del tiempo",
      context: "de un autónomo o gerente de pyme se dedica a tareas administrativas. Documentos, actas, informes, búsquedas de información... Tiempo que debería ir a crecer el negocio."
    },
    blocks: [
      {
        title: "Toda tu información en un solo sitio",
        bigNumber: "1 fuente",
        comparison: "En lugar de 10 carpetas y 5 herramientas",
        description: "Base de conocimiento centralizada con IA. Pregunta y obtén respuestas instantáneas de tus propios documentos.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=gestion-administrativa"
      },
      {
        title: "Actas y resúmenes automáticos",
        bigNumber: "0 min",
        comparison: "Dedicados a tomar notas en reuniones",
        description: "La IA escucha, transcribe y genera actas con puntos clave y tareas asignadas. Tú te concentras en la reunión.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=gestion-administrativa"
      },
      {
        title: "Informes que se escriben solos",
        bigNumber: "-70%",
        comparison: "En tiempo de preparación de informes",
        description: "Genera informes periódicos, análisis y reportes con datos actualizados. Sin copiar y pegar, sin errores.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto?tipo=gestion-administrativa"
      }
    ],
    caseStudy: {
      quote: "Antes perdíamos horas buscando información en emails y carpetas. Ahora le preguntamos a la IA y la respuesta llega en segundos.",
      author: "Director General",
      role: "Consultora de 12 empleados",
      metrics: [
        { value: "-70%", label: "Tiempo búsqueda" },
        { value: "100%", label: "Documentado" },
        { value: "×2", label: "Productividad" }
      ]
    },
    process: [
      { number: "01", title: "Auditamos tu documentación", description: "Revisamos cómo gestionas información, documentos y conocimiento interno." },
      { number: "02", title: "Centralizamos con IA", description: "Creamos tu base de conocimiento inteligente conectada a tus fuentes de datos." },
      { number: "03", title: "Automatizamos lo repetitivo", description: "Actas, informes y resúmenes se generan automáticamente. Tú solo revisas." }
    ],
    comparison: [
      { before: "Buscar en 10 carpetas", after: "Preguntar y obtener" },
      { before: "Tomar notas en reuniones", after: "Actas automáticas" },
      { before: "Informes manuales", after: "Reportes automáticos" }
    ],
    finalCTA: { question: "¿Cuánto tiempo pierdes buscando información?" },
    relatedCategories: ["Gestión Empresarial", "Automatización"],
    seo: {
      title: "Gestión Administrativa Digital con IA | Globaltis IA",
      description: "Bases de conocimiento, actas automáticas e informes inteligentes. Reduce un 70% el tiempo en tareas administrativas con IA."
    }
  },
  "diagnostico-ia": {
    hero: {
      title: "Diagnóstico de Madurez IA.",
      subtitle: "Saber por dónde empezar es la mitad del camino."
    },
    problem: {
      bigStat: "El 80% de las pymes",
      context: "no sabe qué automatizar primero. Hay tantas opciones que la parálisis es inevitable. Sin un diagnóstico claro, se pierde tiempo y dinero en soluciones equivocadas."
    },
    blocks: [
      {
        title: "Mapa completo de oportunidades",
        bigNumber: "100%",
        comparison: "De tus procesos analizados",
        description: "Revisamos cada área de tu negocio para identificar dónde la IA puede generar mayor impacto con menor esfuerzo.",
        ctaText: "Solicitar diagnóstico",
        ctaLink: "/contacto?tipo=diagnostico-ia"
      },
      {
        title: "Hoja de ruta priorizada",
        bigNumber: "3 fases",
        comparison: "Con plazos y resultados esperados",
        description: "No solo te decimos qué hacer, sino en qué orden. Primero lo que más impacto tiene, después el resto.",
        ctaText: "Solicitar diagnóstico",
        ctaLink: "/contacto?tipo=diagnostico-ia"
      },
      {
        title: "ROI estimado por proyecto",
        bigNumber: "€€€",
        comparison: "Ahorro estimado antes de invertir",
        description: "Cada recomendación incluye una estimación de ahorro en tiempo y dinero. Decides con datos, no con intuición.",
        ctaText: "Solicitar diagnóstico",
        ctaLink: "/contacto?tipo=diagnostico-ia"
      }
    ],
    caseStudy: {
      quote: "El diagnóstico nos abrió los ojos. Estábamos intentando automatizar lo menos importante. Ahora tenemos un plan claro y estamos ahorrando desde el primer mes.",
      author: "CEO",
      role: "Empresa de logística",
      metrics: [
        { value: "12", label: "Oportunidades" },
        { value: "3 meses", label: "Plan acción" },
        { value: "×4", label: "ROI primer año" }
      ]
    },
    process: [
      { number: "01", title: "Entrevista inicial", description: "Hablamos contigo para entender tu negocio, tus procesos y tus objetivos a corto y largo plazo." },
      { number: "02", title: "Análisis profundo", description: "Mapeamos tus flujos de trabajo, identificamos cuellos de botella y evaluamos oportunidades de IA." },
      { number: "03", title: "Entrega de hoja de ruta", description: "Recibes un informe completo con recomendaciones priorizadas, plazos y ROI estimado." }
    ],
    comparison: [
      { before: "No saber por dónde empezar", after: "Plan claro en 3 fases" },
      { before: "Invertir sin datos", after: "ROI estimado por proyecto" },
      { before: "Soluciones genéricas", after: "Recomendaciones a medida" }
    ],
    finalCTA: { question: "¿Quieres saber exactamente por dónde empezar?" },
    relatedCategories: ["Gestión Empresarial", "Automatización"],
    seo: {
      title: "Diagnóstico de Madurez IA para Pymes | Globaltis IA",
      description: "Auditoría completa de tu negocio con hoja de ruta personalizada. Descubre qué automatizar primero y cuánto puedes ahorrar con IA."
    }
  }
};

const serviceNames: Record<string, string> = {
  automatizacion: "Automatización de Procesos",
  "atencion-cliente": "Atención al Cliente 24/7",
  "contenido-ia": "Contenido Profesional con IA",
  "presencia-digital": "Presencia Digital Express",
  "gestion-administrativa": "Gestión Administrativa Digital",
  "diagnostico-ia": "Diagnóstico de Madurez IA"
};

export default function ServicioDetail() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const config = serviceConfig[serviceId || ""];
  const serviceName = serviceNames[serviceId || ""];

  useSEO({
    title: config?.seo.title || "Servicios | Globaltis IA",
    description: config?.seo.description || "Servicios de IA para pymes y autónomos.",
    canonical: `https://globaltisia.lovable.app/servicios/${serviceId}`,
    jsonLd: config ? {
      "@context": "https://schema.org",
      "@type": "Service",
      name: serviceName,
      description: config.seo.description,
      provider: {
        "@type": "Organization",
        name: "Globaltis IA"
      }
    } : undefined
  });

  if (!config || !serviceName) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-8">
          <h1 className={`${typography.h2Large} text-foreground`}>Servicio no encontrado</h1>
          <Link
            to="/servicios"
            className="group inline-flex items-center gap-4 text-foreground hover:opacity-60 transition-opacity"
          >
            <span className={typography.ctaLink}>Ver todos los servicios</span>
            <span className="text-2xl md:text-3xl group-hover:translate-x-2 transition-transform">→</span>
          </Link>
        </div>
      </div>
    );
  }

  // Get related articles by category
  const relatedArticles = blogPosts
    .filter(post => config.relatedCategories.includes(post.category))
    .slice(0, 6);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <AppleSection background="black" padding="large">
        <div className="text-center space-y-6 sm:space-y-8 md:space-y-12">
          <h1 className={`${typography.h1} text-white dark:text-black leading-tight px-4`}>
            {config.hero.title}
          </h1>
          <p className={`${typography.h2Medium} text-gray-400 dark:text-gray-600 px-4`}>
            {config.hero.subtitle}
          </p>
        </div>
      </AppleSection>

      {/* El Problema */}
      <AppleSection background="white" padding="large">
        <div className="text-center space-y-6 sm:space-y-8 md:space-y-12">
          <p className={`${typography.h1} text-foreground leading-none px-4`}>
            {config.problem.bigStat}
          </p>
          <p className={`${typography.h2Medium} text-muted-foreground max-w-5xl mx-auto leading-relaxed px-4`}>
            {config.problem.context}
          </p>
        </div>
      </AppleSection>

      {/* Bloques de Solución */}
      {config.blocks.map((block, index) => (
        <AppleSection
          key={block.title}
          background={index % 2 === 0 ? "gray" : "white"}
          padding="large"
        >
          <div className="max-w-6xl mx-auto space-y-8 md:space-y-16">
            <div className="text-center space-y-6 md:space-y-8">
              <h2 className={`${typography.h2Large} text-foreground px-4`}>
                {block.title}
              </h2>
              <div className="space-y-3 md:space-y-4">
                <p className={`${typography.h1} text-foreground`}>
                  {block.bigNumber}
                </p>
                <p className={`${typography.paragraphLarge} text-muted-foreground px-4`}>
                  {block.comparison}
                </p>
              </div>
              <p className={`${typography.paragraphNormal} text-muted-foreground max-w-3xl mx-auto leading-relaxed pt-4 md:pt-8 px-4`}>
                {block.description}
              </p>
            </div>
            <div className="flex justify-center pt-4 md:pt-8">
              <Link
                to={block.ctaLink}
                className="group inline-flex items-center gap-3 md:gap-4 text-foreground hover:opacity-60 transition-opacity"
              >
                <span className={typography.ctaLink}>{block.ctaText}</span>
                <span className="text-xl sm:text-2xl md:text-3xl group-hover:translate-x-2 transition-transform">→</span>
              </Link>
            </div>
          </div>
        </AppleSection>
      ))}

      {/* Sección: Qué podemos hacer por tu empresa */}
      {serviceId === "automatizacion" && (() => {
        // Beneficios orientados al cliente (no cómo, sino qué obtiene)
        const benefitCopy: Record<string, string> = {
          "68": "Monta flujos automáticos sin tocar una línea de código",
          "64": "Tu equipo deja de repetir explicaciones: la IA ya sabe cómo trabajas",
          "50": "Recupera 2 horas al día en tareas que hacías a mano",
          "48": "Tu negocio toma decisiones y actúa sin que estés delante",
          "40": "Cualquier empleado nuevo aprende tus procesos en minutos, no en semanas",
          "38": "Solo ves los mensajes que importan, el resto se gestiona solo",
          "36": "Convierte notas sueltas y audios en documentos listos para enviar",
          "30": "Lanza una web o landing en minutos, no en semanas",
          "21": "Cobros al día, facturas revisadas y competencia vigilada sin mover un dedo",
          "15": "1.000 contactos cualificados listos para tu equipo comercial",
          "24": "Deja de rellenar los mismos datos una y otra vez",
          "26": "Responde a cada cliente en WhatsApp al instante, 24/7",
          "71": "Trámites oficiales resueltos en 10 minutos, no en 3 horas",
          "42": "Bandeja de entrada limpia y datos extraídos sin leer un solo email",
          "46": "Crea automatizaciones explicando lo que necesitas con tus palabras",
          "58": "Multiplica tu capacidad de trabajo sin ampliar plantilla",
          "60": "Automatiza procesos complejos sin depender de un técnico",
          "78": "Pedidos, tareas y emails procesados sin intervención humana",
        };

        const automationArticles = blogPosts.filter(
          p => p.category === "Automatización" && p.businessSolution
        );

        if (automationArticles.length === 0) return null;

        // Categorizar artículos por beneficio principal
        const benefitCategories: Array<{
          id: string;
          icon: React.ReactNode;
          title: string;
          subtitle: string;
          keywords: string[];
        }> = [
          {
            id: "tiempo",
            icon: <Clock className="w-6 h-6 text-foreground" />,
            title: "Ahorro de tiempo",
            subtitle: "Deja de hacer a mano lo que la IA hace en segundos.",
            keywords: ["horas", "tiempo", "repetitiv", "manual", "diarias", "semanales", "minutos", "copiar", "rellenar"]
          },
          {
            id: "ventas",
            icon: <TrendingUp className="w-6 h-6 text-foreground" />,
            title: "Más ventas, menos esfuerzo",
            subtitle: "Captura leads, responde al instante y cierra más.",
            keywords: ["ventas", "leads", "clientes", "comercial", "WhatsApp", "responder", "vender", "prospección", "cobros"]
          },
          {
            id: "organizacion",
            icon: <FolderOpen className="w-6 h-6 text-foreground" />,
            title: "Todo organizado, nada perdido",
            subtitle: "Archivos, datos y procesos en orden sin tocarlo tú.",
            keywords: ["archivos", "organiz", "documentos", "notas", "información", "datos", "desorganiz", "buscar"]
          },
          {
            id: "comunicacion",
            icon: <MessageSquare className="w-6 h-6 text-foreground" />,
            title: "Comunicación en piloto automático",
            subtitle: "Emails, consultas y explicaciones que se resuelven solas.",
            keywords: ["correos", "email", "consultas", "notificaciones", "explicando", "formando", "chats", "comunicación"]
          },
          {
            id: "admin",
            icon: <FileText className="w-6 h-6 text-foreground" />,
            title: "Administración sin burocracia",
            subtitle: "Facturas, formularios y papeleo que se procesan solos.",
            keywords: ["facturas", "formularios", "presupuestos", "administrativ", "declaracion", "fiscal", "cobros", "documentos oficiales"]
          },
          {
            id: "escala",
            icon: <Rocket className="w-6 h-6 text-foreground" />,
            title: "Escala sin contratar",
            subtitle: "Haz el trabajo de un equipo grande con el tuyo pequeño.",
            keywords: ["equipo", "contratar", "competir", "agentes", "paralelo", "escal", "capacidad", "100 agentes"]
          }
        ];

        // Asignar cada artículo a su mejor categoría
        const categorized = new Map<string, typeof automationArticles>();
        benefitCategories.forEach(cat => categorized.set(cat.id, []));

        automationArticles.forEach(article => {
          const text = `${article.businessProblem || ""} ${article.businessSolution || ""}`.toLowerCase();
          let bestMatch = "tiempo"; // fallback
          let bestScore = 0;

          benefitCategories.forEach(cat => {
            const score = cat.keywords.filter(kw => text.includes(kw.toLowerCase())).length;
            if (score > bestScore) {
              bestScore = score;
              bestMatch = cat.id;
            }
          });

          categorized.get(bestMatch)!.push(article);
        });

        // Solo mostrar categorías con artículos
        const activeCategories = benefitCategories.filter(
          cat => (categorized.get(cat.id)?.length || 0) > 0
        );

        return (
          <AppleSection background="white" padding="large">
            <div className="max-w-7xl mx-auto space-y-12 md:space-y-16">
              {/* Cabecera general */}
              <div className="text-center space-y-4 md:space-y-6 px-4">
                <h2 className={`${typography.h1} text-foreground`}>
                  Qué podemos hacer por ti.
                </h2>
                <p className={`${typography.paragraphLarge} text-muted-foreground max-w-3xl mx-auto`}>
                  Todo esto ya funciona. Tú eliges, nosotros lo ponemos en marcha.
                </p>
              </div>

              {/* Layout: columna izquierda (Ahorro de tiempo) + grid 2x2 derecha */}
              {(() => {
                const firstCat = activeCategories.find(c => c.id === "tiempo");
                const restCats = activeCategories.filter(c => c.id !== "tiempo");

                const renderCategory = (cat: typeof activeCategories[0], maxItems?: number) => {
                  const allArticles = categorized.get(cat.id) || [];
                  const articles = maxItems ? allArticles.slice(0, maxItems) : allArticles;
                  return (
                    <div key={cat.id} className="bg-muted/30 rounded-2xl p-6 md:p-8 space-y-5 hover:bg-muted/40 transition-colors duration-500">
                      <div className="space-y-1">
                        {cat.icon}
                        <h3 className="text-lg md:text-xl font-semibold text-foreground">
                          {cat.title}
                        </h3>
                        <p className="text-xs text-muted-foreground">{cat.subtitle}</p>
                      </div>
                      <div className="space-y-1">
                        {articles.map(article => (
                          <Link
                            key={article.id}
                            to={`/blog/${article.slug}`}
                            className="group flex items-center gap-2 py-2.5 px-3 -mx-3 rounded-lg hover:bg-background/60 transition-all duration-300"
                          >
                            <span className="text-sm md:text-base text-foreground/80 group-hover:text-foreground flex-1 leading-snug">
                              {benefitCopy[article.id] || article.businessSolution}
                            </span>
                            <span className="text-muted-foreground/30 group-hover:text-foreground group-hover:translate-x-1 transition-all duration-300 shrink-0 text-sm">
                              →
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                };

                return (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                    {/* Columna izquierda: Ahorro de tiempo - sin límite */}
                    {firstCat && renderCategory(firstCat)}

                    {/* Columna derecha: grid 2x2 con el resto */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {restCats.map(cat => renderCategory(cat))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </AppleSection>
        );
      })()}

      {/* Caso Real */}
      <AppleSection background="black" padding="large">
        <div className="max-w-4xl mx-auto space-y-8 md:space-y-12">
          <p className={`${typography.h2Medium} text-white dark:text-black text-center leading-relaxed px-4`}>
            "{config.caseStudy.quote}"
          </p>
          <div className="text-center space-y-2 px-4">
            <p className={`${typography.paragraphLarge} text-white dark:text-black font-medium`}>
              {config.caseStudy.author}
            </p>
            <p className={`${typography.paragraphBase} text-gray-400 dark:text-gray-600`}>
              {config.caseStudy.role}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 pt-8">
            {config.caseStudy.metrics.map((metric, index) => (
              <div key={index} className="text-center space-y-2">
                <p className={`${typography.h1} text-white dark:text-black`}>
                  {metric.value}
                </p>
                <p className={`${typography.paragraphBase} text-gray-400 dark:text-gray-600`}>
                  {metric.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </AppleSection>

      {/* Proceso */}
      <AppleSection background="white" padding="large">
        <div className="max-w-6xl mx-auto space-y-8 md:space-y-16">
          <h2 className={`${typography.h2Large} text-center text-foreground px-4`}>
            Cómo funciona
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {config.process.map((step) => (
              <div key={step.number} className="text-center space-y-4 md:space-y-6">
                <div className={`${typography.h1} text-foreground`}>
                  {step.number}
                </div>
                <h3 className={`${typography.paragraphLarge} text-foreground`}>
                  {step.title}
                </h3>
                <p className={`${typography.paragraphBase} text-muted-foreground`}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </AppleSection>

      {/* Comparativa */}
      <AppleSplitComparison items={config.comparison} />

      {/* Artículos Relacionados */}
      {relatedArticles.length > 0 && (
        <AppleSection background="white" padding="large">
          <div className="max-w-6xl mx-auto space-y-8 md:space-y-12">
            <h2 className={`${typography.h2Large} text-center text-foreground px-4`}>
              Artículos relacionados
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
              {relatedArticles.map(article => (
                <Link
                  key={article.id}
                  to={`/blog/${article.slug}`}
                  className="group space-y-3 md:space-y-4 hover:opacity-60 transition-opacity"
                >
                  <h3 className={`${typography.paragraphNormal} text-foreground line-clamp-2`}>
                    {article.title}
                  </h3>
                  <p className={`${typography.paragraphBase} text-muted-foreground line-clamp-2`}>
                    {article.description}
                  </p>
                  <div className="flex items-center gap-2 text-foreground pt-2">
                    <span className={typography.paragraphBase}>Ver más</span>
                    <span className="group-hover:translate-x-2 transition-transform">→</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </AppleSection>
      )}

      {/* CTA Final */}
      <AppleSection background="black" padding="large">
        <div className="text-center space-y-8 md:space-y-12 px-4">
          <h2 className={`${typography.h2Large} text-white dark:text-black`}>
            {config.finalCTA.question}
          </h2>
          <div className="flex justify-center pt-8">
            <Link
              to="/contacto"
              className="group inline-flex items-center gap-3 md:gap-4 text-white hover:opacity-60 transition-opacity"
            >
              <span className={typography.ctaLink}>Agendar llamada</span>
              <span className="text-xl sm:text-2xl md:text-3xl group-hover:translate-x-2 transition-transform">→</span>
            </Link>
          </div>
        </div>
      </AppleSection>

      <Footer />
    </div>
  );
}